import { AddButton } from '@/common/buttons/AddButton';
import { EntityList } from '@/common/entityFramework/entityList/EntityList';
import { useRsrc } from '@/common/rsrc/RsrcContext';
import { ExprUtils } from '@/common/utils/ExprUtils';
import { ObjUtils } from '@/common/utils/ObjUtils';
import { useCallback } from 'react';
import { formatFormulaDisplay } from './tier/formFields/TierFormulaUtils';
import { useTierFormulaPalette } from './tier/formFields/useTierFormulaPalette';

// Displays ProgProdTier rows for Program Products.
// Pass parentEntityObj for a single owner or parentEntityIds to view tiers across multiple owners.
export const ProgProdTierList = ({ parentEntityObj, parentEntityIds }) => {
  const rsrc = useRsrc();
  const progProdIds = parentEntityIds ?? (parentEntityObj ? [parentEntityObj.id] : []);
  const { codeToVariable } = useTierFormulaPalette(parentEntityObj?.progId);

  // Collapse tiers under their owning Program Product's code only when viewing across multiple
  // owners (parentEntityIds). A single Program Product owner (Prog Prod detail) stays flat.
  const grouped = !parentEntityObj;

  const augRowsReq = useCallback(
    (rowsReq) => {
      // TIER_LIST resolves the owning Program Product code into progProdCode.
      rowsReq.query = 'TIER_LIST';
      rowsReq.expr = ExprUtils.whereParams('progProdId', progProdIds);
    },
    [progProdIds.join(',')],
  );

  const clientSideRowData = useCallback(
    (setDataSource) => {
      const rowsReq = {
        query: 'TIER_LIST',
        expr: ExprUtils.whereParams('progProdId', progProdIds),
      };
      rsrc.rows('md', 'PROG_PROD_TIER', rowsReq).then((rows) => {
        const tiers = rows ?? [];
        // Supply a parent group row per progProdCode carrying the shared progProdId, so the group
        // row (path = progProdCode) renders those common values rather than an empty auto-generated node.
        const parentByCode = {};
        tiers.forEach((row) => {
          row.path = row.progProdCode + '|' + row.id;
          parentByCode[row.progProdCode] ??= {
            path: row.progProdCode,
            progProdId: row.progProdId,
            progProdCode: row.progProdCode,
          };
        });
        setDataSource([...Object.values(parentByCode), ...tiers]);
      });
    },
    [progProdIds.join(',')],
  );

  const augNavEntityObj = (navEntityObj) => {
    navEntityObj.progProdId = parentEntityObj.id;
  };

  const augFieldDefs = (fieldToFieldDef) => {
    // Highest tier first
    if (fieldToFieldDef.verStartInstant) fieldToFieldDef.verStartInstant.sort = null;
    fieldToFieldDef.tier.sort = 'desc';
    // Show variables by display name and values formatted (ex: $500.00), as the formula builder does
    if (fieldToFieldDef.formula) {
      fieldToFieldDef.formula.valueFormatter = ({ value }) => formatFormulaDisplay(value, codeToVariable);
    }
    // // Keep the entity column alongside a hyperlink to the owning Program Product.
    // if (fieldToFieldDef.progProdId) {
    //   fieldToFieldDef.progProdId = {
    //     ...fieldToFieldDef.progProdId,
    //     type: 'HYPERLINK',
    //     module: 'md',
    //     entity: 'PROG_PROD',
    //     displayName: 'Entity',
    //   };
    // }
    // The tier code column hosts the tree expand/collapse control when grouping tiers
    if (grouped && fieldToFieldDef.code) {
      fieldToFieldDef.code.autoGrouping = true;
    }
  };

  const augTabButtons = (tabButtons) => {
    ObjUtils.clearObj(tabButtons);
    // Adding a tier requires a single owning entity
    if (parentEntityObj) {
      tabButtons.push(<AddButton entity="PROG_PROD_TIER" augNavEntityObj={augNavEntityObj} />);
    }
  };

  return (
    <EntityList
      entity="PROG_PROD_TIER"
      augFieldDefs={augFieldDefs}
      augTabButtons={augTabButtons}
      {...(grouped ? { clientSideRowData } : { augRowsReq })}
      gridOptions={grouped ? { treeData: true, getRowId: (params) => String(params.data?.path) } : {}}
    />
  );
};
